import { Capacitor } from '@capacitor/core';
import { env } from '@config/env';
import { getSessionTenantId, useAuthStore } from '@shared/stores/authStore';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { useNetworkStore } from '@shared/stores/networkStore';
import { useSettingsStore } from '@shared/stores/settingsStore';
import { useExperienceStore } from '@shared/stores/experienceStore';
import { useModuleStore } from '@shared/stores/moduleStore';
import { resolveDomain, loadDomainConfig, domainForResolution, prefersConfiguredDomain, effectiveDefaultDomain } from '@shared/api/domainApi';
import { toApiError } from '@shared/api/errors';
import { useAppStatusStore } from '@shared/stores/appStatusStore';
import { applyLocalDevBootstrap, shouldUseDirectTenantLogin } from '@shared/lib/localDevBootstrap';
import { seedLocalStorageFromPrefs, installDurableMirror } from '@shared/lib/durablePrefs';
import { refreshSession } from '@features/auth/api';
import { initLiveUpdates } from '@shared/ota/liveUpdate';
import { initDeepLinks } from '@shared/lib/deepLinks';
import {
  preloadBrandImages,
  hideWebSplashGate,
  hideNativeSplash,
  delay,
  IMAGE_PRELOAD_TIMEOUT_MS,
  SPLASH_MAX_WAIT_MS,
} from '@shared/lib/appSplash';
import { initKeyboardShift } from './keyboardShift';

/**
 * Splash-gated 10-step app initialization. Mirrors the Angular
 * `AppInitializerService.initialize()` flow, but reorganized to be safely
 * non-blocking where possible. We hide the native splash only after the
 * cheap UI-affecting steps (branding, auth, theme) complete; the slower
 * authenticated tenant fetches continue in the background.
 *
 * Steps in execution order:
 *   1. Apply system theme + reduced-motion preferences.
 *   2. Hydrate persisted Zustand stores (auth refresh, branding, modules,
 *      app experience) so first paint shows last-known state instantly.
 *   3. Detect platform; initialize Capacitor Network listener.
 *   4. Apply default branding placeholder if nothing is cached.
 *   5. Decide which domain to resolve — route hints (signup/onboarding)
 *      can override the hostname for the resolution call.
 *   6. POST /resolve-domain → branding + regional API URL + SSO flags.
 *   7. Re-read the session (`refreshSession`) — token + licence/permissions,
 *      profile, linked communities — so account changes land without a
 *      re-login; 401s mid-session are still handled by the interceptors.
 *   8. If already authenticated, load Tenant API /domain-config →
 *      moduleStore + experienceStore.
 *   9. Hide native splash; React mounts and renders the router.
 *  10. Bind the deep-link listener here; app-state and push listeners are
 *      wired by the App shell (BiometricGate, usePushNotifs).
 */
export async function bootstrap(): Promise<void> {
  const releaseTag = env.gitCommitSha ? ` @ ${env.gitCommitSha}` : '';
  console.info(`[boot] Starting ${env.appName} v${env.version}${releaseTag} (${env.buildMode})`);

  // Live web-bundle OTA (native only): confirm this bundle booted OK and, in the
  // background, pull + stage a newer bundle for this app's template. Fire-and-forget
  // so it never blocks or breaks boot.
  void initLiveUpdates();

  // Universal links (`appUrlOpen`, native only): bound before the first await so a link
  // arriving during boot is still routed. Web deep links are handled by the browser itself.
  initDeepLinks();

  // 0. Native durability backstop. WKWebView localStorage under the custom
  //    scheme origin is not reliably persisted across app kill / reinstall (the
  //    Keychain is), so seed any dropped `paas_*` keys back from Capacitor
  //    Preferences and mirror future writes to it. MUST precede every store
  //    hydrate/write below (authStore, branding re-hydrate, domain resolve) so
  //    last session's tenant context + SSO community list survive a reopen.
  await seedLocalStorageFromPrefs();
  installDurableMirror();
  // brandingStore self-hydrated at import (before this seed ran) — re-read so it
  // picks up restored branding instead of falling back to the hub placeholder.
  useBrandingStore.getState()._hydrate();

  applyLocalDevBootstrap();

  // 1. Theme + motion already applied during settingsStore eager hydrate.
  // Just kick off network listener.
  useNetworkStore.getState().startListening().catch(() => { /* non-fatal */ });

  // 2. Hydrate stores from storage. authStore.hydrate is async (secure storage),
  // others are sync. branding + settings already self-hydrated at module load.
  await useAuthStore.getState().hydrate();
  useExperienceStore.getState().hydrate();
  useModuleStore.getState().hydrate();

  // Booting signed out: drop the previous session's residue before step 5 reads
  // storage to pick a domain — a stale active community there resolves the wrong
  // tenant — and drop the cached experience/config that belonged to it.
  if (!useAuthStore.getState().isAuthenticated()) {
    useAuthStore.getState().clearStaleSession();
    useExperienceStore.getState().setExperience(null);
    useModuleStore.getState().setConfig(null);
  }

  // 2b. The session's tenant is the active tenant. A previous run could have left the ambient
  // tenant pointing at the hub (it is rewritten by every domain resolution), and the member's
  // data lives only in the community that issued their token — so restore it before anything
  // reads it for an `x-tenant-id`.
  {
    const sessionTenantId = getSessionTenantId();
    if (sessionTenantId && sessionTenantId !== useAuthStore.getState().getTenantId()) {
      console.info(`[boot] Restoring session tenant ${sessionTenantId}`);
      useAuthStore.getState().setTenantId(sessionTenantId);
    }
  }

  // 3. Platform detect.
  const platform = Capacitor.getPlatform();
  console.info(`[boot] Platform: ${platform}`);

  // 3a. Diagnostic: post-hydrate auth/SSO context (the state whose loss reverts
  //     branding + empties the Communities nav on reopen).
  {
    const a = useAuthStore.getState();
    let activePortal = '';
    try { activePortal = localStorage.getItem('paas_active_portal_domain') ?? ''; } catch { /* noop */ }
    console.info(
      `[boot] hydrated: authed=${a.isAuthenticated()} tenantId=${a.getTenantId() ?? '∅'} ` +
        `linkedTenants=${a.linkedTenants.length} regionalApi=${a.getRegionalApiUrl() ? 'set' : '∅'} ` +
        `activePortal=${activePortal || '∅'} branding=${useBrandingStore.getState().branding?.name ?? '∅'}`,
    );
  }

  // 3b. Native keyboard: shift the whole UI up instead of resizing/compacting.
  initKeyboardShift();

  // 4. Default branding placeholder if nothing cached.
  const brandingState = useBrandingStore.getState();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const preferredDomain = domainForResolution(hostname);
  if (!brandingState.branding) {
    console.info('[boot] Applying default branding placeholder');
    brandingState.setDefaultBranding(preferredDomain || hostname || env.defaultDomain || 'localhost');
  }

  // 5/6. Resolve the base branding + its domain-config, then preload (cache) the
  // branding images — and only THEN hide the splash. So the first painted screen
  // is fully branded (no default-green flash) with logo/backgrounds already cached
  // on the device. Gate on tenantId only (single-tenant + SSO-root builds resolve a
  // tenantId at boot, so base branding/imagery loads before sign-in too).
  const brandingReady = (async () => {
    const resolvedDomain = await resolveDomainForBoot(hostname);
    if (env.loadDomainConfig && useAuthStore.getState().getTenantId()) {
      await loadDomainConfig(resolvedDomain || preferredDomain || hostname).catch((error) => {
        console.warn('[boot] domain-config load failed (non-fatal):', error);
      });
    }
    await preloadBrandImages(IMAGE_PRELOAD_TIMEOUT_MS);
  })();

  // 7/8. Re-read the session: access token + licence/permissions, profile, and the
  //      linked communities. So a role change, a profile edit, or an approval into a
  //      new community applies on this launch rather than the next sign-in.
  //      Non-blocking: the stores it writes are reactive, so tiles, admin actions and
  //      the Communities list correct themselves as soon as it lands.
  refreshSession();

  // 9. Hide the splash once branding + images are ready — bounded so a slow
  //    network never traps the user behind the splash.
  await Promise.race([brandingReady, delay(SPLASH_MAX_WAIT_MS)]);
  hideWebSplashGate();
  await hideNativeSplash();

  // 11. Re-apply theme to ensure CSS vars + dark class are in sync.
  useSettingsStore.getState().applyTheme();
}

async function resolveDomainForBoot(hostname: string): Promise<string> {
  const route = readRouteHints();

  // Onboarding deep link: resolve the embedded domain rather than the host.
  if (route.onboardingDomainHint) {
    try {
      console.info(`[boot] Resolving onboarding domain: ${route.onboardingDomainHint}`);
      await resolveDomain(route.onboardingDomainHint, { persistSsoFlags: false });
      return route.onboardingDomainHint;
    } catch (error) {
      console.warn('[boot] Onboarding domain resolve failed:', error);
    }
  }

  // /onboarding?tenant=… without domain — adopt that tenant id into the
  // auth store. The `x-tenant-id` header is read from authStore.tenantId,
  // so writing localStorage alone left the header on the base/hub tenant
  // and the onboarding instance lookup hit the wrong tenant DB.
  // setTenantId() updates the store *and* persists to localStorage.
  if (route.onboardingTenantHint && /^[0-9a-f-]{36}$/i.test(route.onboardingTenantHint)) {
    useAuthStore.getState().setTenantId(route.onboardingTenantHint);
    return '';
  }

  // On localhost (dev) or inside a native shell, the window host can't name the
  // tenant — defer to the configured/active domain so we skip Tenant API
  // domain-config until the user selects a community. Core resolve still runs to
  // bring in hub branding.
  const usesConfiguredDomain = prefersConfiguredDomain(hostname);

  // Skip resolution if we already have a regional API URL cached AND we're not
  // on a fresh login route (otherwise we lose hub branding for unauth screens).
  const cachedRegional = useAuthStore.getState().regionalApiUrl;
  if (cachedRegional && !usesConfiguredDomain && !route.signupPath && !route.loginPath) {
    return hostname;
  }

  const target = domainForResolution(hostname);
  if (!target) {
    console.info('[boot] No domain to resolve (domainless dev mode)');
    return '';
  }

  // SBM / single-tenant localhost: tenant + regional API are pinned by
  // applyLocalDevBootstrap — skip Core resolve-domain (avoids 404 when Core is down).
  if (shouldUseDirectTenantLogin() && useAuthStore.getState().getTenantId()) {
    console.info(`[boot] Direct tenant login — skipping Core resolve-domain (${target})`);
    return target;
  }

  try {
    await resolveDomain(target);
    return target;
  } catch (error) {
    // Network errors (status 0), timeouts, and Core 5xx are transient — non-fatal,
    // keep whatever branding is cached. Only a 404 is a definitive "no such tenant".
    if (toApiError(error).status !== 404) {
      console.warn('[boot] Domain resolve failed (non-fatal):', error);
      if (Capacitor.isNativePlatform()) {
        console.warn('[boot] Native + offline: using cached branding only');
      }
      return target;
    }

    // A 404 on a *previously selected community* is stale device state, not a broken
    // app: on native `domainForResolution()` trusts `paas_active_portal_domain`
    // unconditionally, so a domain left behind by another build/vertical gets resolved
    // against this build's Core and 404s. Drop the pointer and retry the pinned domain
    // — blocking here strands the user on an unrecoverable screen (no router, no reset).
    const pinned = effectiveDefaultDomain();
    if (prefersConfiguredDomain(hostname) && pinned && target !== pinned) {
      console.warn(`[boot] resolve-domain 404 for stale active domain "${target}" — falling back to "${pinned}"`);
      try { localStorage.removeItem('paas_active_portal_domain'); } catch { /* noop */ }
      return resolveDomainForBoot(hostname);
    }

    // The build's OWN configured domain doesn't exist — hard-block so the user never
    // lands in a broken empty shell.
    console.error(`[boot] resolve-domain 404 for "${target}" — configured app/tenant does not exist; blocking app`);
    useAppStatusStore.getState().setNotFound(true);
    return target;
  }
}

interface RouteHints {
  signupPath: boolean;
  loginPath: boolean;
  welcomePath: boolean;
  onboardingPath: boolean;
  onboardingDomainHint: string;
  onboardingTenantHint: string;
  /** Unauthenticated auth screens — skip blocking domain resolve at boot. */
  fastAuthRoute: boolean;
  /** Public routes that call tenant API with X-API-Key (no login). */
  publicApiRoute: boolean;
}

function isPublicApiRoute(pathname: string): boolean {
  return (
    pathname.startsWith('/access/invite/') ||
    pathname.startsWith('/access/onboarding/')
  );
}

function readRouteHints(): RouteHints {
  if (typeof window === 'undefined') {
    return {
      signupPath: false,
      loginPath: false,
      welcomePath: false,
      onboardingPath: false,
      onboardingDomainHint: '',
      onboardingTenantHint: '',
      fastAuthRoute: false,
      publicApiRoute: false,
    };
  }
  const pathname = window.location.pathname;
  const path = pathname + window.location.hash;
  const search = new URLSearchParams(window.location.search);
  const onboardingPath = path.includes('/onboarding');
  const loginPath = path.includes('/login');
  const signupPath = path.includes('/signup');
  const welcomePath = path.includes('/welcome');
  return {
    signupPath,
    loginPath,
    welcomePath,
    onboardingPath,
    onboardingDomainHint: onboardingPath ? (search.get('domain')?.trim() ?? '') : '',
    onboardingTenantHint: onboardingPath ? (search.get('tenant')?.trim() ?? '') : '',
    fastAuthRoute: loginPath || signupPath || welcomePath,
    publicApiRoute: isPublicApiRoute(pathname),
  };
}

